// Office HOME (design-system spec §6 UX-0, recomposed by role in P3-UX, D-443).
// It answers "what does my day hold" for whoever opens it, and nothing else:
//   - Role cards (home-day.tsx): the counts of the reader's own work, each a
//     link to the list it counts with that list's filter. A card exists only for
//     a reader who can act on what it counts.
//   - "Needs attention": what went wrong and who must fix it, grouped so one
//     cause is one row (home-attention.ts), at most eight rows before "Show
//     all", each with one action. The reader's own tasks due today or earlier
//     stay a row each, because each completes with one press (spec 09 §4.16).
//   - Workspace setup: shown once, to a reader who runs the wizard, and only
//     until the tenant goes live. The catalogue and plant counts that measure
//     setup live in that card, not on every reader's home.
// Every number is a real read and every read is enabled on the code its own
// endpoint demands (D-040, D-118). Anything the backend cannot answer is absent,
// never faked (D-033). The page reveals once, when every enabled read has
// settled (§5).
//
// MY DAY (spec 09 §4.16, D-313 ruling 8; P3-E2 PR E2-1). The worklist carries
// the reader's own open sales tasks due today or earlier, from ONE read
// (`scope=mine&status=open&due=through_today&limit=50`) enabled on
// `sales.activity.write`. A task row is red when overdue and amber when due
// today, its age says "Overdue 3 d" or "Today" from the tenant's business date,
// and its one action completes it with one press. The card links to My day on
// `/sales/tasks` and, for a reader who can hold tasks, prints NO COUNT: the task
// read is one page whose envelope carries no count (D-289).

import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { client } from "../../../lib/api";
import { Card } from "../../../components/card";
import { PageHeader } from "../../../components/breadcrumbs";
import { StatusChip, type ChipVariant } from "../../../components/status-chip";
import { DataTable, type DataTableColumn } from "../../../components/data-table";
import { Button } from "../../../components/button";
import { EmptyState, Skeleton, TableSkeleton } from "../../../components/states";
import { IconCheckSmall, IconRefresh } from "../../../components/icons";
import {
  countOfWholeList,
  shellCountLabel,
  useCatalogueCountRead,
  type ShellCountRead,
} from "../../../routes/shell-data";
import { useOnboardingPermissions } from "../../onboarding/hooks/permissions";
import { useOnboardingSession } from "../../onboarding/hooks/onboarding-queries";
import { useInventoryPermissions } from "../inventory/hooks/permissions";
import { useProductionPermissions } from "../production/hooks/permissions";
import { useScrapReasons } from "../production/hooks/production-queries";
import { localeLabel } from "../production/lib/format";
import { CHECKLIST_CATALOG } from "../../onboarding/screens/checklist/checklist-catalog";
import { canOpenTasks, useSalesPermissions } from "../sales/hooks/permissions";
import {
  canCompleteTask,
  useCompleteActivity,
  useMyDay,
  type SalesActivity,
} from "../sales/hooks/activity-queries";
import { taskWhat } from "../sales/components/sales-tasks";
import { myDaySearch } from "../sales/screens/tasks-search-params";
import { daysBetweenIso } from "../sales/lib/quotes";
import { facilityTodayIso } from "../../../lib/business-dates";
import { useTenantTimezone } from "../../../lib/role-codes";
import { WIZARD_STEPS, stepStatusOf } from "../../onboarding/lib/wizard-steps";
import {
  ATTENTION_CAP,
  checklistRows,
  conflictRows,
  countRows,
  handoffRows,
  importRows,
  sortAttention,
  visibleAttention,
  type AttentionRow,
  type Severity,
} from "./home-attention";
import { HomeDayCards, useHomeDay } from "./home-day";
import "./home.css";

const LIST_LIMIT = 50;
const STALE_MS = 60_000;

/* ------------------------------- reads ---------------------------------- */

/** The third shell count, in the same shape as the other two so all three
 * tiles render through one decision. `GET /v1/org/work-centers` is the only one
 * of the three that is NOT paginated (`WorkCenterListResponse` is `{ items }`,
 * no cursor), so this read can only ever be exact-or-unknown — `countOfWholeList`
 * is where that claim lives, checked against the contract rather than assumed
 * at the tile. */
function useWorkCenterCount(enabled: boolean): ShellCountRead {
  const q = useQuery({
    queryKey: ["home", "work-centers"],
    enabled,
    retry: false,
    staleTime: STALE_MS,
    queryFn: async ({ signal }) => {
      const { data } = await client.GET("/v1/org/work-centers", { signal });
      return data ?? null;
    },
  });
  const body = q.data ?? undefined;
  return {
    count: countOfWholeList(body?.items),
    isSettled: q.isFetched,
    updatedAt: q.dataUpdatedAt,
  };
}

function useHomeChecklist(enabled: boolean) {
  return useQuery({
    queryKey: ["home", "checklist"],
    enabled,
    retry: false,
    staleTime: STALE_MS,
    queryFn: async ({ signal }) => {
      const { data } = await client.GET("/v1/onboarding/checklist", { signal });
      return data ?? null;
    },
  });
}

function useHomeImports(enabled: boolean) {
  return useQuery({
    queryKey: ["home", "imports"],
    enabled,
    retry: false,
    staleTime: STALE_MS,
    queryFn: async ({ signal }) => {
      const { data } = await client.GET("/v1/imports", {
        signal,
        params: { query: { limit: LIST_LIMIT } },
      });
      return data ?? null;
    },
  });
}

function useHomeCountsInReview(enabled: boolean) {
  return useQuery({
    queryKey: ["home", "counts-review"],
    enabled,
    retry: false,
    staleTime: STALE_MS,
    queryFn: async ({ signal }) => {
      const { data } = await client.GET("/v1/inventory/cycle-counts", {
        signal,
        params: { query: { status: "review", limit: LIST_LIMIT } },
      });
      return data ?? null;
    },
  });
}

function useHomeConflicts(enabled: boolean) {
  return useQuery({
    queryKey: ["home", "conflicts"],
    enabled,
    retry: false,
    staleTime: STALE_MS,
    queryFn: async ({ signal }) => {
      const { data } = await client.GET("/v1/floor/conflicts", {
        signal,
        params: { query: { status: ["open"], limit: LIST_LIMIT } },
      });
      return data ?? null;
    },
  });
}

function useHomeHandoffs(enabled: boolean) {
  return useQuery({
    queryKey: ["home", "handoffs"],
    enabled,
    retry: false,
    staleTime: STALE_MS,
    queryFn: async ({ signal }) => {
      const { data } = await client.GET("/v1/defect-handoffs", {
        signal,
        params: { query: { disposition: "pending", limit: LIST_LIMIT } },
      });
      return data ?? null;
    },
  });
}

/* ------------------------------ worklist -------------------------------- */

const SEVERITY_CHIP: Record<Severity, ChipVariant> = {
  bad: "bad",
  warn: "warn",
  neutral: "neutral",
};

/** "26 min" / "4 h" / "2 d" — coarse on purpose; the row links to the truth. */
export function ageParts(nowMs: number, iso: string): { unit: "min" | "h" | "d"; n: number } {
  const ms = Math.max(0, nowMs - Date.parse(iso));
  const min = Math.floor(ms / 60_000);
  if (min < 60) return { unit: "min", n: min };
  const h = Math.floor(min / 60);
  if (h < 24) return { unit: "h", n: h };
  return { unit: "d", n: Math.floor(h / 24) };
}

/** A My day task's age words (pure, unit-tested; spec 09 §4.16): "Overdue
 * N d" counted from the task's due date to the TENANT's today when the server
 * says it is overdue, else "Today", because My day reads only tasks due today
 * or earlier. */
export function myDayAge(
  row: Pick<SalesActivity, "overdue" | "due_on">,
  tenantToday: string,
): { key: "home.attention.taskOverdue"; days: number } | { key: "home.attention.taskToday" } {
  if (!row.overdue) return { key: "home.attention.taskToday" };
  const days = daysBetweenIso(row.due_on, tenantToday);
  return { key: "home.attention.taskOverdue", days: days !== undefined && days > 0 ? days : 1 };
}

/** What a My day row names beside its words: its most specific subject, the
 * order, else the quote, else the deal, else the customer (pure). A label
 * the reader's door refuses is served null and is skipped. */
export function myDaySubject(
  row: Pick<SalesActivity, "order_no" | "quote_no" | "opportunity_no" | "customer_name">,
): string | undefined {
  return row.order_no ?? row.quote_no ?? row.opportunity_no ?? row.customer_name ?? undefined;
}

/* -------------------------------- screen -------------------------------- */

export function HomeScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const onboardingPerms = useOnboardingPermissions();
  const inventoryPerms = useInventoryPermissions();
  const productionPerms = useProductionPermissions();
  const canOnboarding = onboardingPerms.has("onboarding.view");
  // The wizard's own runner: the reader setup progress, setup checks and the
  // setup counts are for (D-443 ruling 4). Everyone else's home carries none.
  const canRunSetup = onboardingPerms.has("onboarding.manage_wizard");
  /* Worklist reads mirror the code THEIR §5 route demands, not the namespace
   * floor (planner-day audit, 2026-08-17): e25's conflict list requires
   * `production.conflict.resolve` and e18's hand-off queue requires
   * `production.approve_shift_handoff`. D-040's client mirror keeps a doomed
   * request off the wire, and D-118's rule is that a principal without the
   * code sees the worklist WITHOUT those rows. */
  const canResolveConflicts = productionPerms.has("production.conflict.resolve");
  const canDisposeHandoffs = productionPerms.has("production.approve_shift_handoff");
  const canReviewCounts = inventoryPerms.has("inventory.count.review");
  // My day (spec 09 §4.16): the task code's holders only.
  const salesPerms = useSalesPermissions();
  const canMyDay = canOpenTasks(salesPerms);
  const timeZone = useTenantTimezone();

  // The session serves the setup card alone, so only its reader asks for it.
  const readsSession = canOnboarding && canRunSetup;
  const session = useOnboardingSession(readsSession);
  const row = session.data?.session;
  const notLive = row !== undefined && row.status !== "live";
  const showSetup = canRunSetup && notLive;
  const products = useCatalogueCountRead(showSetup);
  const workCenters = useWorkCenterCount(showSetup);
  const checklist = useHomeChecklist(showSetup);
  const imports = useHomeImports(canOnboarding);
  const counts = useHomeCountsInReview(canReviewCounts);
  const conflicts = useHomeConflicts(canResolveConflicts);
  const handoffs = useHomeHandoffs(canDisposeHandoffs);
  // The scrap-reason names serve the hand-off rows alone.
  const scrapReasons = useScrapReasons(canDisposeHandoffs);
  const myDay = useMyDay(canMyDay);
  const myDayFailed = canMyDay && myDay.isError;
  const completeTask = useCompleteActivity();
  const completeTaskMutate = completeTask.mutate;
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const day = useHomeDay();

  const nowMs = Date.now();

  const rows = useMemo<AttentionRow[]>(() => {
    const reasonName = (code: string): string | undefined => {
      const cause = (scrapReasons.data ?? []).find((c) => c.code === code);
      return cause === undefined ? undefined : localeLabel(cause.name_i18n, i18n.language);
    };
    const fallbackAt = session.data?.session.started_at ?? new Date(nowMs).toISOString();
    const out: AttentionRow[] = [
      ...importRows(t, imports.data?.items ?? [], onboardingPerms.canImport),
      ...(showSetup
        ? checklistRows(
            t,
            checklist.data?.items ?? [],
            (key) => CHECKLIST_CATALOG.find((e) => e.key === key)?.fixRoute,
            fallbackAt,
          )
        : []),
      ...countRows(t, counts.data?.items ?? []),
      ...conflictRows(t, conflicts.data?.items ?? []),
      ...handoffRows(t, handoffs.data?.items ?? [], reasonName),
    ];

    const tenantToday = facilityTodayIso(nowMs, timeZone);
    for (const task of myDay.data?.items ?? []) {
      if (task.status !== "open") continue;
      const age = myDayAge(task, tenantToday);
      out.push({
        key: `task-${task.id}`,
        severity: task.overdue ? "bad" : "warn",
        bucket: "task",
        item: taskWhat(t, task),
        detail: myDaySubject(task) ?? t("sales.tasks.subjectNone"),
        // The due date orders tasks among themselves; the severity sort puts
        // every overdue task above today's.
        at: `${task.due_on}T00:00:00.000Z`,
        actionLabel: t("home.attention.taskComplete"),
        ageText:
          age.key === "home.attention.taskOverdue"
            ? t("home.attention.taskOverdue", { count: age.days })
            : t("home.attention.taskToday"),
        task,
      });
    }
    return sortAttention(out);
  }, [
    imports.data,
    checklist.data,
    counts.data,
    conflicts.data,
    handoffs.data,
    scrapReasons.data,
    myDay.data,
    session.data,
    showSetup,
    onboardingPerms.canImport,
    timeZone,
    t,
    i18n.language,
    nowMs,
  ]);

  const visible = visibleAttention(rows, showAll);
  const hidden = rows.length - visible.length;

  const columns: DataTableColumn<AttentionRow>[] = [
    {
      key: "type",
      header: t("home.attention.colType"),
      kind: "status",
      render: (r) => (
        <StatusChip variant={SEVERITY_CHIP[r.severity]}>
          {t(`home.attention.type${r.bucket.charAt(0).toUpperCase()}${r.bucket.slice(1)}`)}
        </StatusChip>
      ),
    },
    { key: "item", header: t("home.attention.colItem"), id: true, render: (r) => r.item },
    {
      key: "detail",
      header: t("home.attention.colDetail"),
      render: (r) => <span className="ds-td-detail">{r.detail}</span>,
    },
    {
      key: "age",
      header: t("home.attention.colAge"),
      age: true,
      render: (r) => {
        if (r.ageText !== undefined) {
          return (
            <span
              className={r.severity === "bad" ? "home-task-age-bad" : "home-task-age-warn"}
              data-testid="home-task-age"
            >
              {r.ageText}
            </span>
          );
        }
        const a = ageParts(nowMs, r.at);
        return t(`home.age.${a.unit}`, { count: a.n });
      },
    },
    {
      key: "action",
      header: t("home.attention.colAction"),
      actions: true,
      render: (r) => {
        if (r.task !== undefined) {
          // ONE action per row, as every row has (the worklist's contract).
          const task = r.task;
          if (!canCompleteTask(salesPerms, task, true)) return null;
          const busy = completeTask.isPending && completingTaskId === task.id;
          const failed = completeTask.isError && completingTaskId === task.id;
          return (
            <span className="home-task-action">
              <button
                type="button"
                className="ds-lnk"
                data-testid="home-task-complete"
                data-task-id={task.id}
                disabled={completeTask.isPending}
                aria-busy={busy ? true : undefined}
                onClick={() => {
                  setCompletingTaskId(task.id);
                  completeTaskMutate(task.id);
                }}
              >
                {busy ? t("home.attention.taskCompleting") : r.actionLabel}
              </button>
              {failed ? (
                <span className="home-task-failed" role="alert" data-testid="home-task-failed">
                  {t("home.attention.taskCompleteFailed")}
                </span>
              ) : null}
            </span>
          );
        }
        const link = r.link;
        if (link === undefined) return null;
        return (
          <button
            type="button"
            className="ds-lnk"
            onClick={() => {
              void navigate(link);
            }}
          >
            {r.actionLabel}
          </button>
        );
      },
    },
  ];

  /* ---- the §5 zero-CLS gate ------------------------------------------------
   * The home's LAYOUT is decided by reads (which role cards exist, whether the
   * setup rail exists, what the worklist holds), so the content reveals ONCE:
   * until every enabled read has settled (fetched or failed, never an eternal
   * skeleton) the screen is a reserving skeleton (§3.9), and the reveal
   * remounts the whole subtree (`key` swap) so nothing painted early moves.
   * Each read waits only behind its own gate: a disabled query never fetches,
   * so waiting on it would hold the skeleton forever. */
  const permsResolved =
    onboardingPerms.isResolved &&
    inventoryPerms.isResolved &&
    productionPerms.isResolved &&
    salesPerms.isResolved;
  const ready =
    permsResolved &&
    (!readsSession || session.isFetched) &&
    day.settled &&
    (!showSetup || (products.isSettled && workCenters.isSettled && checklist.isFetched)) &&
    (!canResolveConflicts || conflicts.isFetched) &&
    (!canDisposeHandoffs || (handoffs.isFetched && scrapReasons.isFetched)) &&
    (!canOnboarding || imports.isFetched) &&
    (!canReviewCounts || counts.isFetched) &&
    (!canMyDay || myDay.isFetched);

  /* ---- setup card ---- */
  const summary = session.data?.checklist_summary;
  const steps = session.data?.steps ?? [];
  const nextStep = WIZARD_STEPS.find((s) => stepStatusOf(steps, s.stepNo) !== "completed");

  const updatedAtMs = Math.max(
    imports.dataUpdatedAt,
    checklist.dataUpdatedAt,
    counts.dataUpdatedAt,
    conflicts.dataUpdatedAt,
    handoffs.dataUpdatedAt,
    myDay.dataUpdatedAt,
    session.dataUpdatedAt,
    products.updatedAt,
    workCenters.updatedAt,
    day.updatedAt,
  );
  const updatedLabel =
    updatedAtMs > 0
      ? new Date(updatedAtMs).toLocaleTimeString(i18n.language, {
          hour: "2-digit",
          minute: "2-digit",
        })
      : undefined;

  const refreshAction = (
    <button
      type="button"
      className="ds-iconbtn"
      aria-label={t("home.refresh")}
      data-testid="home-refresh"
      onClick={() => {
        void queryClient.invalidateQueries({ queryKey: ["home"] });
        void queryClient.invalidateQueries({ queryKey: ["shell"] });
      }}
    >
      <IconRefresh />
    </button>
  );

  if (!ready) {
    // Reserving skeleton (§3.9): the page head, a strip of card slots and the
    // worklist card hold the final geometry. A distinct `key` forces a clean
    // remount at reveal.
    return (
      <div className="home-screen" data-testid="home-skeleton" key="home-skeleton">
        <PageHeader
          title={t("home.title")}
          crumbs={[{ label: t("home.crumbRoot"), to: "/home" }]}
          actions={refreshAction}
        />
        <div className="home-day" aria-hidden="true">
          {[0, 1].map((i) => (
            <Card key={i} title={<Skeleton width="96px" height="12px" />}>
              <div className="ds-cbody">
                <Skeleton width="100%" height="14px" />
                <Skeleton width="80%" height="14px" />
              </div>
            </Card>
          ))}
        </div>
        <div className="home-cols">
          <Card title={t("home.attention.title")}>
            <TableSkeleton rows={6} cols={5} label={t("home.loading")} />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="home-screen" data-testid="home-screen" key="home-ready">
      <PageHeader
        title={t("home.title")}
        crumbs={[{ label: t("home.crumbRoot"), to: "/home" }]}
        meta={updatedLabel !== undefined ? t("home.updated", { time: updatedLabel }) : undefined}
        actions={refreshAction}
      />

      <HomeDayCards day={day} />

      <div className="home-cols">
        <Card
          title={t("home.attention.title")}
          // No count for a reader who can hold tasks: My day's read is one
          // page with no count on its envelope (the header says why).
          count={canMyDay ? undefined : rows.length}
          data-testid="home-attention"
        >
          {/* A failed My day read is one line, never the all-clear below: the
              card cannot say nothing needs attention over tasks it could not
              see. The footer still opens My day. */}
          {myDayFailed ? (
            <p className="home-my-day-failed" role="status" data-testid="home-my-day-failed">
              {t("home.attention.myDayFailed")}
            </p>
          ) : null}
          <DataTable
            columns={columns}
            rows={visible}
            rowKey={(r) => r.key}
            caption={t("home.attention.title")}
            clientPager={{
              range: (from, to, total) => t("home.attention.range", { from, to, total }),
              prevLabel: t("home.attention.prevPage"),
              nextLabel: t("home.attention.nextPage"),
            }}
            empty={
              myDayFailed ? null : (
                <EmptyState
                  title={t("home.attention.emptyTitle")}
                  // A reader who holds no tasks is not told their tasks are
                  // clear: the card never read any for them (§4.16).
                  body={
                    canMyDay ? t("home.attention.emptyBody") : t("home.attention.emptyBodyNoTasks")
                  }
                />
              )
            }
          />
          {hidden > 0 || showAll || canMyDay ? (
            <p className="home-attention-foot">
              {hidden > 0 ? (
                <button
                  type="button"
                  className="ds-link home-attention-more"
                  data-testid="home-attention-all"
                  onClick={() => {
                    setShowAll(true);
                  }}
                >
                  {t("home.attention.showAll", { count: rows.length })}
                </button>
              ) : null}
              {showAll && rows.length > ATTENTION_CAP ? (
                <button
                  type="button"
                  className="ds-link home-attention-more"
                  data-testid="home-attention-fewer"
                  onClick={() => {
                    setShowAll(false);
                  }}
                >
                  {t("home.attention.showFewer")}
                </button>
              ) : null}
              {canMyDay ? (
                <Link
                  to="/sales/tasks"
                  search={myDaySearch()}
                  className="ds-link"
                  data-testid="home-open-my-day"
                >
                  {t("home.attention.openMyDay")}
                </Link>
              ) : null}
            </p>
          ) : null}
        </Card>

        {showSetup ? (
          <div className="home-rail">
            <Card
              title={t("home.setup.title")}
              data-testid="home-setup"
              action={
                summary !== undefined ? (
                  <StatusChip variant="accent" dot={false}>
                    {summary.score}%
                  </StatusChip>
                ) : null
              }
            >
              <ul className="ds-steps">
                {WIZARD_STEPS.map((step) => {
                  const status = stepStatusOf(steps, step.stepNo);
                  const cls =
                    status === "completed"
                      ? "ds-step-done"
                      : step.stepNo === (nextStep?.stepNo ?? -1)
                        ? "ds-step-cur"
                        : "ds-step-todo";
                  return (
                    <li key={step.stepNo} className={cls}>
                      <span className="ds-st" aria-hidden="true">
                        {status === "completed" ? (
                          <IconCheckSmall />
                        ) : cls === "ds-step-cur" ? (
                          <i />
                        ) : null}
                      </span>
                      {t(step.titleKey)}
                    </li>
                  );
                })}
              </ul>
              {/* The two counts that measure setup, here and nowhere else:
                  a live tenant's home does not carry them. Three states each,
                  as everywhere a page count shows (routes/shell-data.ts). */}
              <ul className="home-day-list home-setup-counts">
                <li>
                  <Link
                    to="/catalogue/products"
                    className="home-day-row"
                    data-testid="kpi-products"
                  >
                    <span className="home-day-label">{t("home.setup.products")}</span>
                    <span className="home-day-count num">
                      {shellCountLabel(products.count, t) ?? t("count.unknown")}
                    </span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/onboarding/plant"
                    className="home-day-row"
                    data-testid="kpi-work-centers"
                  >
                    <span className="home-day-label">{t("home.setup.workCenters")}</span>
                    <span className="home-day-count num">
                      {shellCountLabel(workCenters.count, t) ?? t("count.unknown")}
                    </span>
                  </Link>
                </li>
              </ul>
              {nextStep !== undefined ? (
                <div className="ds-cpad">
                  <Button
                    variant="primary"
                    data-testid="home-setup-continue"
                    onClick={() => {
                      if (nextStep.to !== null) void navigate({ to: nextStep.to });
                    }}
                  >
                    {t("home.setup.continue", { step: t(nextStep.titleKey) })}
                  </Button>
                </div>
              ) : null}
            </Card>
          </div>
        ) : null}
      </div>
    </div>
  );
}
